import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { apiClient, formatApiErrorDetail } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = unknown, false = not auth, object = auth
  const [loading, setLoading] = useState(true);

  const fetchMe = useCallback(async () => {
    try {
      const { data } = await apiClient.get("/auth/me");
      setUser(data);
    } catch (e) {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  const login = async (email, password) => {
    try {
      await apiClient.post("/auth/login", { email, password });
      await fetchMe();
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e) || "Login failed. Please try again." };
    }
  };

  const employeeLogin = async (email, password) => {
    try {
      await apiClient.post("/auth/employee/login", { email, password });
      await fetchMe();
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e) || "Login failed. Please try again." };
    }
  };

  const register = async (payload) => {
    try {
      await apiClient.post("/auth/register", payload);
      await fetchMe();
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e) || "Could not create account. Please try again." };
    }
  };

  const acceptInvite = async (code, email, password) => {
    try {
      await apiClient.post("/auth/employee/accept-invite", { code, email, password });
      await fetchMe();
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e) || "Invite could not be accepted." };
    }
  };

  const logout = async () => {
    try {
      await apiClient.post("/auth/logout");
    } catch (e) {
      // Best-effort — even if the network call fails we still clear session locally.
    }
    setUser(false);
    // #1 — Hard-redirect to /login (not /) so the landing page never shows for
    // signed-out users AND the browser Back button can't restore any
    // authenticated page from the bfcache (window.location.replace overwrites
    // the current history entry).
    if (typeof window !== "undefined") {
      window.location.replace("/login");
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, employeeLogin, register, acceptInvite, logout, refresh: fetchMe }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
